<template>
  <b-container class="py-4" style="max-width: 860px">
    <div class="d-flex align-items-center mb-3">
      <b-button variant="light" :to="{ name: 'CajasRegalo' }">← Volver</b-button>
      <h2 class="mb-0 ml-3">{{ isNew ? "Nueva caja" : "Editar caja" }}</h2>
    </div>

    <b-form @submit.prevent="guardar">
      <b-card class="mb-3">
        <b-form-group label="Slug (identificador en la URL)" description="Solo minúsculas, números y guiones. No cambiar una vez publicada.">
          <b-form-input v-model="caja.slug" :disabled="!isNew" placeholder="ej. escapada-natural" @input="normalizarSlug" required />
        </b-form-group>

        <I18nField
          label="Nombre"
          type="text"
          :value-es="caja.nombre"
          :value-en="ingles.nombre"
          :value-pt="portugues.nombre"
          :manual-override="ov('nombre')"
          @change="(l, v) => setI18n('nombre', l, v)"
        />
        <I18nField
          label="Tagline (frase corta)"
          type="text"
          :value-es="caja.tagline"
          :value-en="ingles.tagline"
          :value-pt="portugues.tagline"
          :manual-override="ov('tagline')"
          @change="(l, v) => setI18n('tagline', l, v)"
        />
        <I18nField
          label="Descripción corta"
          type="textarea"
          :value-es="caja.descripcionCorta"
          :value-en="ingles.descripcionCorta"
          :value-pt="portugues.descripcionCorta"
          :manual-override="ov('descripcionCorta')"
          @change="(l, v) => setI18n('descripcionCorta', l, v)"
        />
      </b-card>

      <b-card class="mb-3">
        <b-row>
          <b-col md="4">
            <b-form-group label="Precio (CLP, 2 personas)">
              <b-form-input v-model.number="caja.precio" type="number" min="0" required />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Noches">
              <b-form-input v-model.number="caja.noches" type="number" min="0" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Orden (menor = primero)">
              <b-form-input v-model.number="caja.orden" type="number" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Ocasiones (slugs)" description="ej. aniversario, luna-de-miel, cumpleanos. Enter para agregar.">
          <b-form-tags v-model="caja.ocasiones" separator=" ," placeholder="Agregar ocasión..." />
        </b-form-group>

        <b-form-checkbox v-model="caja.destacada" switch class="mb-2">Destacada (badge “MÁS REGALADA”)</b-form-checkbox>
        <b-form-checkbox v-model="caja.publicada" switch>Publicada</b-form-checkbox>
        <b-alert :show="caja.publicada && nAlt < 8" variant="warning" class="mt-2 mb-0">
          ⚠️ Esta caja está publicada con solo <strong>{{ nAlt }}</strong> alternativas (se recomiendan 8+).
        </b-alert>
      </b-card>

      <b-card class="mb-3" title="Imagen de portada">
        <div v-if="caja.imagenPortada" class="mb-2">
          <img :src="caja.imagenPortada" alt="portada" style="max-width: 240px; border-radius: 8px" />
        </div>
        <b-form-file accept="image/*" @change="subirImagen" :disabled="subiendo" />
        <small v-if="subiendo" class="text-muted">Subiendo…</small>
        <b-form-group label="o pega una URL" class="mt-2">
          <b-form-input v-model="caja.imagenPortada" placeholder="https://..." />
        </b-form-group>
      </b-card>

      <b-card class="mb-3" title="Condiciones">
        <b-row>
          <b-col md="6">
            <b-form-group label="Validez (meses)">
              <b-form-input v-model.number="caja.condiciones.validezMeses" type="number" min="1" />
            </b-form-group>
          </b-col>
          <b-col md="6" class="d-flex align-items-center">
            <b-form-checkbox v-model="caja.condiciones.cambioSinCosto" switch>Cambio sin costo por otra alternativa</b-form-checkbox>
          </b-col>
        </b-row>
        <b-form-group label="Suplemento fin de semana (texto, si aplica)">
          <b-form-input v-model="caja.condiciones.suplementoFinDeSemana" placeholder="Opcional" />
        </b-form-group>
        <b-form-group label="Suplemento temporada alta (texto, si aplica)">
          <b-form-input v-model="caja.condiciones.suplementoTemporadaAlta" placeholder="Opcional" />
        </b-form-group>
        <I18nField
          label="Qué incluye (a nivel caja)"
          type="textarea"
          :value-es="caja.condiciones.incluyeTexto"
          :value-en="inglesCond.incluyeTexto"
          :value-pt="portuguesCond.incluyeTexto"
          :manual-override="ov('cond.incluyeTexto')"
          @change="(l, v) => setCondI18n('incluyeTexto', l, v)"
        />
      </b-card>

      <div class="d-flex justify-content-between">
        <b-button type="submit" variant="primary" :disabled="guardando">{{ guardando ? "Guardando…" : "Guardar caja" }}</b-button>
      </div>
    </b-form>

    <!-- Versiones asignadas (solo en edición) -->
    <b-card v-if="!isNew" class="mt-4" :title="`Alternativas asignadas (${versiones.length})`">
      <p class="text-muted">
        Las versiones se crean desde cada escapada con “Duplicar como versión de caja” y se asignan a esta caja por su slug.
      </p>
      <b-alert :show="versiones.length < 8" variant="warning">Menos de 8 alternativas: no se recomienda publicar.</b-alert>
      <b-table
        v-if="versiones.length"
        :items="versiones"
        :fields="[
          { key: 'nombre', label: 'Versión' },
          { key: 'noches', label: 'Noches' },
          { key: 'publicada', label: 'Estado' },
          { key: 'acciones', label: '' },
        ]"
        small
        responsive
      >
        <template #cell(publicada)="r">
          <b-badge :variant="r.item.publicada ? 'success' : 'secondary'">{{ r.item.publicada ? "Publicada" : "Borrador" }}</b-badge>
        </template>
        <template #cell(acciones)="r">
          <b-button size="sm" variant="outline-primary" :to="{ name: 'EditVersionCaja', params: { padreId: r.item.padreId, vid: r.item.id } }">Editar</b-button>
        </template>
      </b-table>
    </b-card>
  </b-container>
</template>

<script>
import { db, storage } from "../../firebase.service";
import firebase from "../../firebase.service";
import I18nField from "../../components/I18nField.vue";

function emptyCaja() {
  return {
    slug: "",
    nombre: "",
    tagline: "",
    descripcionCorta: "",
    imagenPortada: "",
    precio: 0,
    noches: 0,
    ocasiones: [],
    orden: 0,
    destacada: false,
    publicada: false,
    condiciones: { validezMeses: 12, cambioSinCosto: true, suplementoFinDeSemana: "", suplementoTemporadaAlta: "", incluyeTexto: "" },
    ingles: {},
    portugues: {},
    manualOverride: {},
  };
}

export default {
  name: "EditCajaRegalo",
  components: { I18nField },
  data() {
    return { caja: emptyCaja(), versiones: [], guardando: false, subiendo: false };
  },
  computed: {
    isNew() {
      return this.$route.name === "NuevaCajaRegalo";
    },
    ingles() { return this.caja.ingles || {}; },
    portugues() { return this.caja.portugues || {}; },
    inglesCond() { return (this.caja.ingles && this.caja.ingles.condiciones) || {}; },
    portuguesCond() { return (this.caja.portugues && this.caja.portugues.condiciones) || {}; },
    nAlt() {
      return this.versiones.filter((v) => v.publicada).length;
    },
  },
  async created() {
    if (!this.isNew) {
      const slug = this.$route.params.slug;
      const doc = await db.collection("cajas").doc(slug).get();
      if (doc.exists) {
        this.caja = { ...emptyCaja(), ...doc.data(), slug: doc.id };
        if (!this.caja.condiciones) this.caja.condiciones = emptyCaja().condiciones;
      }
      this.cargarVersiones(slug);
    }
  },
  methods: {
    normalizarSlug() {
      this.caja.slug = (this.caja.slug || "")
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
    },
    ov(field) {
      return (this.caja.manualOverride && this.caja.manualOverride[field]) || { en: false, pt: false };
    },
    markOv(field, lang) {
      const cur = this.caja.manualOverride[field] || { en: false, pt: false };
      this.$set(this.caja.manualOverride, field, { ...cur, [lang]: true });
    },
    setI18n(field, lang, value) {
      if (lang === "es") this.$set(this.caja, field, value);
      else if (lang === "en") this.$set(this.caja.ingles, field, value);
      else this.$set(this.caja.portugues, field, value);
      if (lang !== "es") this.markOv(field, lang);
    },
    setCondI18n(field, lang, value) {
      if (lang === "es") this.$set(this.caja.condiciones, field, value);
      else {
        const branch = lang === "en" ? "ingles" : "portugues";
        if (!this.caja[branch].condiciones) this.$set(this.caja[branch], "condiciones", {});
        this.$set(this.caja[branch].condiciones, field, value);
        this.markOv("cond." + field, lang);
      }
    },
    async subirImagen(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const slug = this.caja.slug || "sin-slug";
      this.subiendo = true;
      try {
        const ref = storage.ref(`cajas/${slug}/portada_${Date.now()}_${file.name}`);
        const snap = await ref.put(file);
        this.caja.imagenPortada = await snap.ref.getDownloadURL();
      } catch (err) {
        alert("No se pudo subir la imagen: " + (err && err.message));
      } finally {
        this.subiendo = false;
      }
    },
    async cargarVersiones(slug) {
      try {
        const snap = await db.collectionGroup("versiones").where("cajaSlugs", "array-contains", slug).get();
        this.versiones = snap.docs.map((d) => ({ id: d.id, padreId: d.ref.parent.parent.id, ...d.data() }));
      } catch (e) {
        // Si el índice de collectionGroup+where no existe, cargamos todas y filtramos.
        try {
          const snap = await db.collectionGroup("versiones").get();
          this.versiones = snap.docs
            .map((d) => ({ id: d.id, padreId: d.ref.parent.parent.id, ...d.data() }))
            .filter((v) => Array.isArray(v.cajaSlugs) && v.cajaSlugs.includes(slug));
        } catch (e2) {
          console.warn("[EditCajaRegalo] versiones fallido:", e2 && e2.message);
        }
      }
    },
    async guardar() {
      if (!this.caja.slug) { alert("El slug es obligatorio."); return; }
      this.guardando = true;
      try {
        const data = { ...this.caja };
        delete data.id;
        data.ts = firebase.firestore.FieldValue.serverTimestamp();
        await db.collection("cajas").doc(this.caja.slug).set(data, { merge: true });
        this.$router.push({ name: "CajasRegalo" });
      } catch (e) {
        alert("No se pudo guardar: " + (e && e.message));
      } finally {
        this.guardando = false;
      }
    },
  },
};
</script>
